import Cookies from 'js-cookie'
import { Fragment, useCallback, useEffect, useState } from 'react'
import { Card } from 'react-bootstrap'
import config from '../../config'
import useDebounce from '../../hooks/useDebounce'
import { INITIAL_SEARCH_PARAMS, PAYMENT_STATUS, PRE_ORDER_TYPE_LABEL } from './constants'
import { PageContainer, StatusText, StyledTable } from './styles'
import { getBackgroundColorByStatus, getTextColorByStatus } from './utils'
import moment from 'moment'
import { Pagination } from '../Payment/components/Pagination'
import PreOrderFilter from './components/PreorderFilter'

const PreOrder = () => {
  const [preOrders, setPreOrders] = useState([])
  const [hasNextPage, setHasNextPage] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [searchParams, setSearchParams] = useState(INITIAL_SEARCH_PARAMS)
  const fetchPreOrder = useCallback(async (searchParams) => {
    setIsLoading(true)
    try {
      const params = new URLSearchParams(searchParams)

      searchParams.orderNo === '' && params.delete('orderNo')

      const response = await fetch(`${config.apiAdminGateway}/api/v1/admin/preorder?${params}`, {
        headers: {
          Authorization: `Bearer ${Cookies.get('accessToken')}`,
        },
      }).then((res) => res.json())

      if (response.status.code !== 0) throw new Error(response.status.description)
      setPreOrders(response.data)
      setHasNextPage(response.pagination.hasNext)
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const debouncedFetchPreOrder = useDebounce(fetchPreOrder, 500)

  const handleUpdateSearchParams = useCallback(
    (key, value) => {
      switch (key) {
        case 'status':
          setSearchParams((params) => ({
            ...params,
            page: 1,
            status: params.status === value ? PAYMENT_STATUS.UNKNOWN : value,
          }))
          break
        case 'roundDate':
          setSearchParams((params) => ({
            ...params,
            page: 1,
            roundDate: config.roundDate,
          }))
          break
        case 'phoneNo':
          if (/[^0-9]/g.test(value)) return
          setSearchParams((params) => ({
            ...params,
            page: 1,
            orderNo: '',
            number: '',
            userNo: '',
            firstName: '',
            lastName: '',
            phoneNo: value,
          }))
          break

        case 'number':
          setSearchParams((params) => ({
            ...params,
            page: 1,
            orderNo: '',
            userNo: '',
            firstName: '',
            lastName: '',
            phoneNo: '',
            number: value,
          }))
          break

        case 'orderNo':
        case 'userNo':
        case 'firstName':
        case 'lastName':
          setSearchParams((params) => ({
            ...params,
            page: 1,
            orderNo: '',
            number: '',
            userNo: '',
            firstName: '',
            lastName: '',
            phoneNo: '',
            [key]: value,
          }))
          break

        default:
          if (searchParams[key] === value) return

          setSearchParams((params) => ({
            ...params,
            page: 1,
            [key]: value,
          }))
          break
      }
    },
    [searchParams],
  )

  const handleClickResetFilter = () => {
    setSearchParams(INITIAL_SEARCH_PARAMS)
  }

  const handleClickPagination = (isNext) => {
    const page = isNext ? searchParams.page + 1 : searchParams.page - 1
    setIsLoading(true)
    setSearchParams((params) => ({ ...params, page: page }))
  }

  const formatNumberLottryPreOrder = (number) => {
    return number?.replaceAll('.', 'X')
  }

  useEffect(() => {
    debouncedFetchPreOrder(searchParams)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  return (
    <PageContainer>
      <Card>
        <Card.Header>
          <Card.Title>รายการจองลอตเตอรี่</Card.Title>
        </Card.Header>

        <Card.Header>
          <PreOrderFilter
            searchParams={searchParams}
            fetchPayments={debouncedFetchPreOrder}
            onUpdateSearchParams={handleUpdateSearchParams}
            onClickResetFilter={handleClickResetFilter}
          />
        </Card.Header>
        <Card.Body
          style={{
            transition: 'background-color 200ms ease-in-out',
            backgroundColor: getBackgroundColorByStatus(searchParams.status),
          }}
        >
          <StyledTable>
            <thead>
              <tr>
                <th>เลขที่ออเดอร์</th>
                <th>วันที่จอง</th>
                <th>สถานะ</th>
                <th>ประเภท</th>
                <th>เลขที่จอง</th>
                <th>รหัสลูกค้า</th>
                <th>ชื่อลูกค้า</th>
                <th>จองทั้งหมด</th>
                <th>จองสำเร็จ</th>
                <th>หมายเหตุ</th>
              </tr>
            </thead>
            <tbody>
              {preOrders.length > 0 ? (
                preOrders.map((preOrder) => {
                  return (
                    <tr key={preOrder.id}>
                      <td>{preOrder.orderNo}</td>
                      <td>{moment(preOrder.createdAt).format('DD/MM/YYYY')}</td>
                      <td>
                        <StatusText color={getTextColorByStatus(preOrder.status)}>{preOrder.status}</StatusText>
                      </td>
                      <td>{PRE_ORDER_TYPE_LABEL[preOrder.type]}</td>
                      <td>{formatNumberLottryPreOrder(preOrder.number)}</td>
                      <td>{preOrder.userNo}</td>
                      <td>
                        {preOrder.firstName} {preOrder.lastName}
                      </td>
                      <td>{preOrder.expectedAmt} ใบ</td>
                      <td>{preOrder.successAmt} ใบ</td>
                      {preOrder.status === 'CANCELLED' ? (
                        <Fragment>
                          {preOrder.status === 'CANCELLED' && preOrder.roundDate !== '' ? (
                            <td>ระบบยกเลิก</td>
                          ) : (
                            <td>
                              <div>ลูกค้ายกเลิกเมื่อ</div>
                              <div>{moment(preOrder.updatedAt).format('HH:mm DD/MM/YYYY')}</div>
                            </td>
                          )}
                        </Fragment>
                      ) : (
                        <td></td>
                      )}
                    </tr>
                  )
                })
              ) : (
                <tr style={{ pointerEvents: 'none' }}>
                  <td
                    colSpan={99}
                    style={{
                      textAlign: 'center',
                      color: 'red',
                      fontSize: '1.25rem',
                    }}
                  >
                    ไม่มีออเดอร์
                  </td>
                </tr>
              )}
            </tbody>
          </StyledTable>
        </Card.Body>
        <Card.Footer>
          <Pagination
            pagination={{ page: searchParams.page, hasNext: hasNextPage }}
            disabled={isLoading}
            onClickPagination={handleClickPagination}
          />
        </Card.Footer>
      </Card>
    </PageContainer>
  )
}

export default PreOrder
